
import React, { useState, useEffect, useRef } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import InfoIcon from "@mui/icons-material/Info";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ConnectOwnProfile from "layouts/dashboard/components/ConnectOwnProfile";
import ProfileCard from "./ProfileCard";
import { useFetchProfiles } from "./hooks/useFetchProfiles";
import ProfileDropdown from "./ProfileDropdown";
import DayRangeSelector from "./DayRangeSelector";
import AuthService from "services/auth-service";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { Typography } from "@mui/material";
import Slide from "@mui/material/Slide";
import WorkspaceService from "services/workspace-service";

import UpgradePopup from "layouts/components/Upgrade";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo";

import { usePermissions } from "context"; // [NEW]

function KeyPerformanceIndicators() {
  const [showConnectProfile, setShowConnectProfile] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState("last30Days");
  const { loading, error, currentData, previousData } =
    useFetchProfiles(selectedDateRange);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const cardContentRef = useRef(null);

  // [NEW] Usage Enforcement State
  const [usageRaw, setUsageRaw] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showDemoPopup, setShowDemoPopup] = useState(false); // [NEW] Demo Popup State

  // [NEW] RBAC State
  const [showRBAC, setShowRBAC] = useState(false);
  const { hasPermission, requiredRoles } = usePermissions();

  // Dynamic navbar height tracker
  const [navHeight, setNavHeight] = useState(75);

  // [NEW] Demo User Flag (Top Level)
  const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";

  useEffect(() => {
    const navbarEl = document.querySelector("header.MuiAppBar-root");
    if (navbarEl) setNavHeight(navbarEl.offsetHeight);

    const resizeObserver = new ResizeObserver(() => {
      if (navbarEl) setNavHeight(navbarEl.offsetHeight);
    });

    if (navbarEl) resizeObserver.observe(navbarEl);
    return () => resizeObserver.disconnect();
  }, []);

  // Custom Styled Tooltip
  const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: 400,
      textAlign: "left",
    },
  });

  const tooltipContent = (
    <div style={{ maxWidth: "350px", textAlign: "left" }}>

      {/* Description */}
      <Typography variant="body2" gutterBottom>
        <strong>Description:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        This section provides a high-level overview of your brand&apos;s performance across connected social platforms.
      </Typography>

      {/* How to Read */}
      <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
        <strong>How to read the dashboard:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        - Compare <strong>current metrics</strong> against previous periods (indicated by percentage changes). <br />
        - Hover over <strong>percentages</strong> to see exact dates and comparative values. <br />
        - Metrics are aggregated or platform-specific.
      </Typography>

      {/* Insights */}
      <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
        <strong>Insights you can gain:</strong>
      </Typography>
      <Typography variant="caption" display="block">
        - Identify growth trends in followers and reach. <br />
        - Measure the effectiveness of your content strategy through engagement rates. <br />
        - Spot sudden drops or spikes in performance instantly.
      </Typography>
    </div>
  );

  // Fetch email verification status
  useEffect(() => {
    const getUserVerificationStatus = async () => {
      try {
        const response = await AuthService.getProfile();
        const emailVerifiedAt = response.data.attributes.email_verified_at;
        setIsEmailVerified(emailVerifiedAt !== null);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    getUserVerificationStatus();
    fetchUsageData(); // [NEW] Fetch usage on mount
  }, []);

  // [NEW] Fetch Usage Logic
  const fetchUsageData = async () => {
    try {
      const entitlements = await WorkspaceService.fetchWorkspaceEntitlements();
      if (!entitlements) return;

      setUsageRaw({
        profilesUsed: entitlements.usage?.profiles || 0,
        profilesLimit: entitlements.limits?.profiles || 0,
        currentPlan: entitlements.plan || "Basic"
      });
    } catch (error) {
      console.error("Error fetching usage details:", error);
    }
  };

  // Auto popup + initial selection
  useEffect(() => {
    if (currentData.length > 0 && selectedProfiles.length === 0) {
      setSelectedProfiles(currentData.map((p) => p.profileId));
    }

    // [RESTORED] Auto-popup for regular users, bypassed for demo
    if (!loading && isEmailVerified && currentData.length === 0 && !isDemo) {
      setShowConnectProfile(true);
    }

    if (currentData.length > 0) {
      setShowConnectProfile(false);
    }

    if (!loading) {
      setDataLoaded(true);
    }
  }, [currentData, loading, isEmailVerified]);

  const handleAddProfileClick = () => {
    // [NEW] RBAC Check
    if (!hasPermission("stash:manage")) {
      setShowRBAC(true);
      return;
    }

    // [NEW] Live Demo Restriction
    if (isDemo) {
      setShowDemoPopup(true);
      return;
    }

    // [NEW] Check Usage Limit
    if (usageRaw) {
      const { profilesUsed, profilesLimit } = usageRaw;
      if (Number(profilesUsed) >= Number(profilesLimit)) {
        setShowConnectProfile(false);
        setShowUpgrade(true);
        return;
      }
    }
    setShowConnectProfile(true);
  };
  const handleCloseConnectProfile = () => setShowConnectProfile(false);

  const handleDateRangeChange = (newDateRange) => {
    setSelectedDateRange(newDateRange);
  };

  return (
    <>
      {/* Sticky header */}
      {dataLoaded && (currentData.length > 0 || isDemo) ? (
        <MDBox
          sx={{
            mt: -6,
            mb: 4,
            position: "sticky",
            top: `${navHeight}px`,
            zIndex: 999,
            backgroundColor: "#ffffff",
            padding: "16px",
            borderRadius: "8px",
            gap: 2,
          }}
        >
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <MDBox display="flex" flexWrap="wrap" gap={2}>
              <DayRangeSelector
                selectedDateRange={selectedDateRange}
                onChange={handleDateRangeChange}
              />
              <ProfileDropdown
                currentData={currentData}
                selectedProfiles={selectedProfiles}
                setSelectedProfiles={setSelectedProfiles}
              />
            </MDBox>

            <MDBox>
              <MDTypography
                variant="button"
                fontWeight="medium"
                color="text"
                sx={{ mb: 1, textAlign: "center" }}
              >
                Connect
              </MDTypography>
              <MDBox
                sx={{
                  borderRadius: "16px",
                  backgroundColor: "#f0f2f5",
                  padding: "14px",
                }}
              >
                <MDBox
                  sx={{
                    width: "fit-content",
                    "&:hover .jitter-card": {
                      transform: "translateY(-4px)",
                      boxShadow: 4,
                    },
                  }}
                >
                  <Card
                    className="jitter-card"
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "row",
                      gap: 1,
                      padding: "4px 18px",
                      borderRadius: "12px",
                      backgroundColor: "grey.100",
                      border: "1px solid #ddd",
                      transition: "all 0.3s ease-in-out",
                    }}
                    onClick={handleAddProfileClick}
                  >
                    <IconButton color="primary" sx={{ p: 0 }}>
                      <AddCircleIcon fontSize="large" />
                    </IconButton>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Add Own Profile
                    </Typography>
                  </Card>
                </MDBox>
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      ) : null}

      {/* Main KPI cards */}
      {dataLoaded && (currentData.length > 0 || isDemo) && (
        <Card sx={{ width: "100%" }}>
          <MDBox>
            <MDBox
              mx={2}
              mt={-3}
              py={1}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              flexDirection="row"
              alignItems="center"
            >
              <MDTypography variant="h6" color="white">
                Key Performance Indicators
              </MDTypography>
              <CustomTooltip title={tooltipContent} placement="bottom" arrow>
                <IconButton sx={{ color: "white", p: 0, ml: 0.5 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </CustomTooltip>
            </MDBox>

            {loading ? (
              <MDBox display="flex" justifyContent="center" alignItems="center" py={5}>
                <CircularProgress color="accent" />
              </MDBox>
            ) : error ? (
              <MDBox display="flex" justifyContent="center" py={5}>
                <MDTypography color="error">{error}</MDTypography>
              </MDBox>
            ) : (
              <MDBox
                ref={cardContentRef}
                px={2}
                py={0}
                sx={{ maxHeight: "470px", overflowY: "auto" }}
              >
                {selectedProfiles.length === 0 ? (
                  <MDBox sx={{ p: "100px" }}>
                    <MDTypography variant="subtitle1" color="text" align="center">
                      {isDemo
                        ? "Your private profile is not connected in Live Demo Mode"
                        : "No profile selected"}
                    </MDTypography>
                  </MDBox>
                ) : (
                  currentData
                    .filter((p) => selectedProfiles.includes(p.profileId))
                    .map((profile) => {
                      const prev = previousData.find(
                        (x) => x.profileId === profile.profileId
                      );
                      return (
                        <Slide
                          key={profile.profileId}
                          in={true}
                          direction="down"
                          container={cardContentRef.current}
                          timeout={800}
                        >
                          <MDBox mb={2}>
                            <ProfileCard profile={profile} previousData={prev} />
                          </MDBox>
                        </Slide>
                      );
                    })
                )}
              </MDBox>
            )}
          </MDBox>
        </Card>
      )}

      {/* Popup */}
      {isEmailVerified && dataLoaded && showConnectProfile && (
        <ConnectOwnProfile
          open={showConnectProfile}
          onClose={handleCloseConnectProfile}
          canClose={currentData.length > 0}   // ✅ NEW PROP ADDED
        />
      )}

      {/* [NEW] Upgrade Popup */}
      <UpgradePopup
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        type="usage"
        featureName="Tracked Profiles"
        planRequired="Higher Plan"
        currentPlan={usageRaw?.currentPlan}
        usageLimit={usageRaw?.profilesLimit}
      />

      {/* [NEW] Live Demo Restriction Popup */}
      <LiveDemoRestriction
        open={showDemoPopup}
        onClose={() => setShowDemoPopup(false)}
        featureName="Connecting Your Profile"
      />

      {/* [NEW] RBAC Restriction Popup */}
      <UpgradePopup
        open={showRBAC}
        onClose={() => setShowRBAC(false)}
        type="rbac"
        featureName="Connecting Profiles"
        roleRequired={requiredRoles["stash:manage"] || "Manager"}
      />
    </>
  );
}

export default KeyPerformanceIndicators;
